import { useState, useEffect } from "react";
const BASE = "https://support.gs-group.fr/api";
const h = () => ({ Authorization:"Bearer "+localStorage.getItem("nexo_token") });

const BAR_COLORS = ["#4361EE","#7B61FF","#10b981","#f59e0b","#ef4444","#3b82f6","#8b5cf6","#06b6d4"];

export default function KmDashboard({ T, vehicules=[] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("semaine");
  const [manquants, setManquants] = useState([]);
  const [selVeh, setSelVeh] = useState(null);
  const [vehHistory, setVehHistory] = useState([]); // semaine | mois | vehicules
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${BASE}/km-analytics/dashboard`, {headers:h()}).then(r=>r.json()).catch(()=>null),
      fetch(`${BASE}/km-analytics/manquants`, {headers:h()}).then(r=>r.json()).catch(()=>[]),
    ]).then(([d, m]) => {
      if(d) setData(d);
      setManquants(Array.isArray(m)?m:[]);
      setLoading(false);
    });
  }, []);

  const openVehHistory = async(veh) => {
    setSelVeh(veh);
    const hist = await fetch(`${BASE}/km-analytics/${veh.id}`,{headers:h()}).then(r=>r.json()).catch(()=>[]);
    setVehHistory(Array.isArray(hist)?hist:[]);
  };

  const exportPdf = async () => {
    setExportingPdf(true);
    try {
      const res = await fetch(`${BASE}/pdf/km-rapport/generate`, {headers:h()});
      if(!res.ok) throw new Error(`Erreur ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch(e) { alert("Erreur lors de la génération du PDF : "+e.message); }
    setExportingPdf(false);
  };

  if(loading) return <div style={{textAlign:"center",padding:60,color:T.muted}}>⏳ Chargement...</div>;
  if(!data) return <div style={{textAlign:"center",padding:60,color:T.muted}}>Aucune donnée</div>;

  const chartData = view==="semaine" ? data.parSemaine : data.parMois;
  const maxKm = Math.max(...chartData.map(d=>parseInt(d.total_km)||0), 1);
  const coutCarburant = data.coutCarburant||[];
  const coutParVehicule = new Map(coutCarburant.map(c=>[c.vehiculeId, c]));
  const totalCoutSemaine = coutCarburant.reduce((s,c)=>s+c.coutEstime,0);

  const fmtKm = n => { if(n>=1000000) return Math.round(n/1000000)+"M"; if(n>=1000) return Math.round(n/1000)+"k"; return Math.round(n)+""; };
  const fmtDate = (row) => {
    if(view==="semaine") return `S${row.semaine}`;
    return new Date(row.mois).toLocaleDateString("fr-FR",{month:"short",year:"2-digit"});
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {/* En-tête + export PDF */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{fontSize:15,fontWeight:800,color:T.text}}>🚗 Kilométrage</div>
        <button onClick={exportPdf} disabled={exportingPdf}
          style={{padding:"9px 16px",borderRadius:9,border:"none",background:`linear-gradient(135deg,${T.accent},${T.purple||"#7B61FF"})`,color:"#fff",fontSize:13,fontWeight:700,cursor:exportingPdf?"wait":"pointer"}}>
          {exportingPdf?"Génération…":"📄 Export PDF hebdomadaire"}
        </button>
      </div>
      {/* Bannière km manquants */}
      {manquants.length>0&&(
        <div style={{background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.3)",borderRadius:12,padding:"14px 18px",display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:20}}>📍</span>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:"#f59e0b"}}>⚠️ {manquants.length} véhicule(s) sans relevé kilométrique cette semaine</div>
            <div style={{fontSize:11,color:"#92400e",marginTop:4}}>{manquants.map(v=>`${v.marque} ${v.modele} (${v.immatriculation})`).join(" · ")}</div>
          </div>
        </div>
      )}
      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10}}>
        {[
          {lbl:"Véhicules suivis", val:data.parVehicule.length, ic:"🚗", c:"#4361EE"},
          {lbl:"Total km enregistrés", val:fmtKm(data.parVehicule.reduce((s,v)=>s+(parseInt(v.km_total_releves)||0),0))+"km", ic:"📍", c:"#10b981"},
          {lbl:"Relevés ce mois", val:data.parMois.slice(-1)[0]?.nb_vehicules||0, ic:"📋", c:"#f59e0b"},
          {lbl:"Alertes km", val:data.anomalies.length, ic:"⚠️", c:data.anomalies.length>0?"#ef4444":"#10b981"},
          {lbl:`Carburant estimé/sem. (${data.prixCarburant?.toFixed(2)||"—"}€/L)`, val:coutCarburant.length?totalCoutSemaine.toLocaleString("fr-FR",{minimumFractionDigits:0,maximumFractionDigits:0})+"€":"—", ic:"⛽", c:"#8b5cf6"},
        ].map(k=>(
          <div key={k.lbl} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:"14px 16px",borderTop:`3px solid ${k.c}`}}>
            <div style={{fontSize:11,color:T.muted,marginBottom:4}}>{k.ic} {k.lbl}</div>
            <div style={{fontSize:22,fontWeight:800,color:k.c}}>{k.val}</div>
          </div>
        ))}
      </div>

      {/* Toggle vue + graphique */}
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"16px 18px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:700,color:T.text}}>📊 Kilométrage total parc</div>
          <div style={{display:"flex",gap:4,background:T.bg,borderRadius:8,padding:3}}>
            {[["semaine","Par semaine"],["mois","Par mois"]].map(([v,l])=>(
              <button key={v} onClick={()=>setView(v)}
                style={{padding:"5px 12px",borderRadius:6,border:"none",background:view===v?T.accent:"transparent",color:view===v?"#fff":T.muted,fontSize:12,fontWeight:view===v?700:500,cursor:"pointer"}}>
                {l}
              </button>
            ))}
          </div>
        </div>
        {/* Graphique barres */}
        <div style={{display:"flex",alignItems:"flex-end",gap:6,height:140,padding:"0 4px"}}>
          {chartData.map((row,i)=>{
            const km = parseInt(row.total_km)||0;
            const pct = maxKm>0 ? (km/maxKm*100) : 0;
            return(
              <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                <div style={{fontSize:9,color:T.muted,fontWeight:600}}>{fmtKm(km)}</div>
                <div title={`${km} km`}
                  style={{width:"100%",background:`linear-gradient(180deg,${T.accent},${T.accent}90)`,borderRadius:"4px 4px 0 0",height:`${Math.max(pct,2)}%`,minHeight:4,transition:"height 0.4s ease",cursor:"pointer"}}
                  onMouseEnter={e=>{e.currentTarget.style.opacity="0.85";}}
                  onMouseLeave={e=>{e.currentTarget.style.opacity="1";}}/>
                <div style={{fontSize:9,color:T.muted,whiteSpace:"nowrap"}}>{fmtDate(row)}</div>
              </div>
            );
          })}
          {chartData.length===0&&<div style={{flex:1,textAlign:"center",color:T.muted,fontSize:12}}>Aucun relevé</div>}
        </div>
      </div>

      {/* Par véhicule */}
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"16px 18px"}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>🚗 Détail par véhicule</div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {data.parVehicule.map((v,i)=>{
            const km = parseInt(v.km_total_releves)||0;
            const max = Math.max(...data.parVehicule.map(x=>parseInt(x.km_total_releves)||0),1);
            const cout = coutParVehicule.get(v.id);
            return(
              <div key={v.id} onClick={()=>openVehHistory(v)} style={{display:"flex",alignItems:"center",gap:12,cursor:"pointer",padding:"4px 8px",borderRadius:8,margin:"0 -8px",transition:"background 0.12s"}} onMouseEnter={e=>e.currentTarget.style.background=T.bg} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <div style={{width:120,fontSize:12,fontWeight:600,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v.marque} {v.modele}</div>
                <div style={{fontSize:10,color:T.muted,width:80,fontFamily:"monospace"}}>{v.immatriculation}</div>
                <div style={{flex:1,height:8,background:T.bg,borderRadius:99,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${km/max*100}%`,background:BAR_COLORS[i%BAR_COLORS.length],borderRadius:99,transition:"width 0.5s ease"}}/>
                </div>
                <div style={{fontSize:12,fontWeight:700,color:BAR_COLORS[i%BAR_COLORS.length],width:60,textAlign:"right"}}>{fmtKm(km)}km</div>
                <div style={{fontSize:10,color:T.muted,width:50,textAlign:"right"}}>{v.nb_releves} relevé{v.nb_releves>1?"s":""}</div>
                {cout&&<div title={`${cout.kmSemaine}km cette semaine × ${cout.consommationMoyenne}L/100km`} style={{fontSize:10,fontWeight:700,color:"#8b5cf6",width:60,textAlign:"right"}}>⛽ {cout.coutEstime.toLocaleString("fr-FR",{minimumFractionDigits:0,maximumFractionDigits:0})}€</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Anomalies */}
      {data.anomalies.length>0&&(
        <div style={{background:"rgba(239,68,68,0.05)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:14,padding:"16px 18px"}}>
          <div style={{fontSize:13,fontWeight:700,color:"#ef4444",marginBottom:12}}>⚠️ Kilométrages suspects ({data.anomalies.length})</div>
          {data.anomalies.map((a,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 12px",background:"#fff",borderRadius:8,marginBottom:6,fontSize:12}}>
              <span style={{fontWeight:700,color:T.text}}>{a.marque} {a.modele}</span>
              <span style={{color:T.muted,fontFamily:"monospace"}}>{a.immatriculation}</span>
              <span style={{color:T.muted}}>{new Date(a.date1).toLocaleDateString("fr-FR")} → {new Date(a.date2).toLocaleDateString("fr-FR")}</span>
              <span style={{fontWeight:800,color:"#ef4444",marginLeft:"auto"}}>+{a.delta.toLocaleString()} km</span>
              <span style={{fontSize:10,color:"#ef4444",background:"#ef444415",padding:"2px 8px",borderRadius:99}}>Seuil: {a.seuil} km</span>
            </div>
          ))}
        </div>
      )}
      {/* Panel historique véhicule */}
      {selVeh&&(
        <div style={{position:"fixed",inset:0,background:"rgba(8,12,30,0.3)",backdropFilter:"blur(4px)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={()=>setSelVeh(null)}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:16,width:"min(600px,95vw)",maxHeight:"80vh",display:"flex",flexDirection:"column",boxShadow:"0 24px 80px rgba(0,0,0,0.18)",overflow:"hidden",animation:"nx-modal-open 0.3s cubic-bezier(0.16,1,0.3,1) both"}}>
            <div style={{padding:"18px 22px",borderBottom:"1px solid #e2e8f0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:16,fontWeight:800,color:"#0f1428"}}>{selVeh.marque} {selVeh.modele}</div>
                <div style={{fontFamily:"monospace",fontSize:14,color:"#4361EE",fontWeight:700}}>{selVeh.immatriculation}</div>
              </div>
              <button onClick={()=>setSelVeh(null)} style={{background:"none",border:"none",fontSize:20,color:"#94a3b8",cursor:"pointer"}}>✕</button>
            </div>
            <div style={{flex:1,overflowY:"auto",padding:"16px 22px"}}>
              <div style={{fontSize:12,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:12}}>Historique kilométrique ({vehHistory.length} relevés)</div>
              {vehHistory.length===0&&<div style={{textAlign:"center",padding:30,color:"#94a3b8"}}>Aucun relevé</div>}
              {vehHistory.map((r,i)=>{
                const prev = vehHistory[i+1];
                const delta = prev ? r.kilometrage - prev.kilometrage : null;
                return(
                  <div key={r.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:"1px solid #f1f5f9"}}>
                    <div style={{fontSize:13,color:"#64748b",width:80,flexShrink:0}}>{new Date(r.date_releve).toLocaleDateString("fr-FR",{day:"2-digit",month:"short"})}</div>
                    <div style={{fontSize:16,fontWeight:800,color:"#0f1428",flex:1}}>{r.kilometrage.toLocaleString()} km</div>
                    {delta!==null&&<div style={{fontSize:12,fontWeight:700,color:delta>5000?"#ef4444":delta>0?"#10b981":"#94a3b8"}}>+{delta.toLocaleString()} km</div>}
                    {r.source==="ocean"?<div style={{fontSize:11,color:"#4361EE",fontWeight:700}}>🛰️ Auto Océan</div>:r.technicien_nom&&<div style={{fontSize:11,color:"#94a3b8"}}>👤 {r.technicien_nom}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
